/**
 * Shared PAM breadcrumb component.
 *
 * Exports:
 *   PamBreadcrumbs — renders a breadcrumb trail from an array of crumbs.
 *                    Terminal crumb is plain text; others are real <a href>
 *                    links wired to client-side navigation.
 *
 * Migrated off MUI (OIDCPAM Gate-6 Phase 3) onto @devnw/layout's
 * `nav/Breadcrumb` — a styled-components breadcrumb whose theme reads are
 * all optional-chained with literal fallbacks (verified against its source),
 * so it renders safely with or without a styled-components ThemeProvider.
 *
 * Breadcrumb renders non-terminal items with an `href` as a real anchor
 * (preserving right-click/ctrl-click "open in new tab" and a real `.href`
 * for any DOM inspection/tests), while a plain left-click is intercepted to
 * navigate client-side via react-router instead of a full page reload —
 * the same trade-off react-router's own <Link> makes internally.
 */
import React from 'react'
import { useNavigate } from 'react-router-dom'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import { Breadcrumb } from '@devnw/layout/nav/Breadcrumb'
import type { BreadcrumbItem } from '@devnw/layout/nav/Breadcrumb'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Crumb {
  /** Display text for this breadcrumb segment */
  label: string
  /**
   * Route path. When omitted (or for the last crumb) the segment is rendered
   * as plain text, not a link.
   */
  to?: string
}

// ---------------------------------------------------------------------------
// PamBreadcrumbs
// ---------------------------------------------------------------------------

interface PamBreadcrumbsProps {
  /** Ordered array of crumbs — last entry is the current page (no link) */
  crumbs: Crumb[]
}

/** Client-side navigate on a plain left click; let modified clicks (ctrl/cmd/
 *  shift/alt/middle-click) fall through to the real `href` so the browser's
 *  native "open in new tab" still works — mirrors react-router's <Link>. */
function makeClientNavHandler(
  navigate: ReturnType<typeof useNavigate>,
  to: string
): (event: React.MouseEvent<HTMLAnchorElement | HTMLSpanElement>) => void {
  return (event) => {
    const mouseEvent = event as React.MouseEvent<HTMLAnchorElement>
    if (
      event.defaultPrevented ||
      mouseEvent.button !== 0 ||
      mouseEvent.metaKey ||
      mouseEvent.ctrlKey ||
      mouseEvent.shiftKey ||
      mouseEvent.altKey
    ) {
      return
    }
    event.preventDefault()
    navigate(to)
  }
}

export const PamBreadcrumbs = ({ crumbs }: PamBreadcrumbsProps): JSX.Element => {
  const navigate = useNavigate()

  const items: BreadcrumbItem[] = crumbs.map((crumb) => ({
    label: crumb.label,
    href: crumb.to,
    onClick: crumb.to ? makeClientNavHandler(navigate, crumb.to) : undefined,
  }))

  return <Breadcrumb items={items} />
}
